import { User, Users } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { theme } from '../theme/theme'

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${theme.spacing.md};
`

const Container = styled.div`
  max-width: 700px;
  width: 100%;
  padding: ${theme.spacing.xl};
`

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xl};
  align-items: center;
`

const Logo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: contain;
`

const LogoText = styled.h1`
  font-size: ${theme.typography.xl};
  font-family: 'Inter', sans-serif;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
`

const Title = styled.h1`
  font-size: ${theme.typography.xxl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-bottom: ${theme.spacing.sm};
`

const Subtitle = styled.p`
  font-size: ${theme.typography.md};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`

const RoleContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: 640px) {
    flex-direction: column;
  }
`

interface RoleCardProps {
  $selected: boolean
}

const RoleCard = styled.button<RoleCardProps>`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 3px solid ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.border)};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  cursor: pointer;
  transition: ${theme.transitions.default};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}40`};
  }
`

const RoleIconContainer = styled.div<{ $selected: boolean }>`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.lg};
  background-color: ${({ theme, $selected }) => ($selected ? `${theme.colors.secondary}20` : `${theme.colors.cardBackground}`)};
  margin-bottom: ${theme.spacing.sm};

  svg {
    color: ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.textMuted)};
  }
`

const RoleTitle = styled.h3<{ $selected: boolean }>`
  font-size: ${theme.typography.xl};
  font-weight: 700;
  color: ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.text)};
  margin-bottom: ${theme.spacing.xs};
`

const RoleDescription = styled.p`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  line-height: 1.5;
`

const ContinueButton = styled.button<{ disabled: boolean }>`
  width: 250px;
  background-color: ${({ theme, disabled }) => (disabled ? theme.colors.border : theme.colors.primary)};
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  margin-left: auto;
  margin-right: auto;
  display: block;
  color: ${({ disabled }) => (disabled ? '#666' : '#000')};
  font-size: ${theme.typography.lg};
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: ${theme.transitions.default};

  &:hover {
    background-color: ${({ theme, disabled }) => (disabled ? theme.colors.border : theme.colors.primaryDark)};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ disabled, theme }) => (disabled ? 'none' : `0 0 0 3px ${theme.colors.primary}40`)};
  }
`

const BackLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  text-decoration: underline;
  cursor: pointer;
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.md};
  width: 100%;
  text-align: center;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

type UserRole = 'coach' | 'athlete'

const RoleSelectionPage: React.FC = () => {
  const navigate = useNavigate()
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null)

  const handleContinue = () => {
    if (selectedRole) {
      navigate(`/signup?role=${selectedRole}`)
    }
  }

  const handleBack = () => {
    navigate('/login')
  }

  return (
    <PageContainer>
      <Container>
        <LogoContainer>
          <Logo src="/logo_512.png" alt="supacoach Logo" />
          <LogoText>supacoach</LogoText>
        </LogoContainer>

        <Title>Are you coach or athlete?</Title>
        <Subtitle>Pick your primary role, it can be changed later.</Subtitle>

        <RoleContainer>
          <RoleCard $selected={selectedRole === 'athlete'} onClick={() => setSelectedRole('athlete')} type="button">
            <RoleIconContainer $selected={selectedRole === 'athlete'}>
              <User size={32} />
            </RoleIconContainer>
            <RoleTitle $selected={selectedRole === 'athlete'}>Athlete</RoleTitle>
            <RoleDescription>
              Plan and analyse your workouts, track your progress. Optionally communicate with a coach.
            </RoleDescription>
          </RoleCard>

          <RoleCard $selected={selectedRole === 'coach'} onClick={() => setSelectedRole('coach')} type="button">
            <RoleIconContainer $selected={selectedRole === 'coach'}>
              <Users size={32} />
            </RoleIconContainer>
            <RoleTitle $selected={selectedRole === 'coach'}>Coach</RoleTitle>
            <RoleDescription>
              Plan and review workouts, communicate with athletes, and guide them to success
            </RoleDescription>
          </RoleCard>
        </RoleContainer>

        <ContinueButton disabled={!selectedRole} onClick={handleContinue}>
          Continue
        </ContinueButton>

        <BackLink onClick={handleBack}>Back to login</BackLink>
      </Container>
    </PageContainer>
  )
}

export default RoleSelectionPage
